import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  computed,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import {
  AbstractControl,
  FormsModule,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { Modal } from '@spirit-testing/component-library';
import { ModalEscapeGuardDirective } from '../../components/modal-escape-guard/modal-escape-guard.directive';

import { TenantUserService } from '../../services/tenant-user.service';
import { CustomerDetailService } from '../../services/customer-detail.service';
import { AccountService } from '../../account/account.service';
import { CustomerDTO, InviteUserRequest, RoleDTO } from '../../models/customer.model';
import { MAIL_NOT_DELIVERED_SNACKBAR } from '../../shared/invitation-mail-snackbar';
import { AvatarComponent } from '../../components/avatar/avatar.component';
import { StatusPillComponent } from '../../components/status-pill/status-pill.component';
import { RoleBadgeComponent } from '../../components/role-badge/role-badge.component';
import { ASSIGNABLE_TENANT_ROLE_CODES } from '../../shared/tenant-roles';
import { emailDomain, isDomainAllowed } from '../../shared/email-domain';
import { createFormDirtyTracker } from '../../shared/forms/dirty-tracker';
import { warnBeforeUnload } from '../../shared/forms/warn-before-unload';
import { detectBrowserTimezone, listTimezones } from '../../shared/time-zone.util';

const FIRST_LAST_NAME_MAX = 100;
const EMAIL_MAX = 255;

// Sprache: kuratierte, design-konforme Auswahl (Design: Deutsch/English).
// Persistiert/gesendet wird der ISO-639-1-Code (de/en), nicht das Anzeige-Label —
// so muss kein Consumer lokalisierte Namen zurückmappen und es passt zum
// bestehenden Locale-Modell des BE (Accept-Language, InvitationMailService). Das
// Label bleibt reine FE-Anzeige.
interface LanguageOption {
  code: string;
  label: string;
}

const LANGUAGE_OPTIONS: readonly LanguageOption[] = [
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
];
// Default bewusst fix auf Deutsch (Produktentscheidung, Carina) — nicht mehr aus
// navigator.language abgeleitet. Eine explizite Nutzerwahl überschreibt das weiterhin.
const DEFAULT_LANGUAGE = LANGUAGE_OPTIONS[0].code;

// Aktueller UTC-Offset der Zone (DST-korrekt zum heutigen Datum), z. B. "GMT+1".
// Quelle ist erneut die Plattform-tz-DB (ECMA-402), keine eigene Offset-Tabelle.
// Ergebnis pro Zone gecacht: das Template ruft dies für die volle ~400-Zonen-Liste
// je Change-Detection-Lauf auf, und der Intl.DateTimeFormat-Konstruktor ist teuer.
// Der Offset ändert sich innerhalb einer Session nicht.
const offsetCache = new Map<string, string>();

function timezoneOffset(timezone: string): string {
  const cached = offsetCache.get(timezone);
  if (cached !== undefined) return cached;
  let result = '';
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date());
    const name = parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
    // Normalisiert "GMT" (UTC±0) zu "GMT+0" für eine einheitliche Darstellung.
    result = name === 'GMT' ? 'GMT+0' : name;
  } catch {
    result = '';
  }
  offsetCache.set(timezone, result);
  return result;
}

type InviteTab = 'daten' | 'rolle' | 'praeferenzen';

interface PermissionItem {
  key: string;
  labelKey: string;
  default: boolean;
}

interface PermissionSection {
  id: string;
  labelKey: string;
  icon: string;
  items: readonly PermissionItem[];
}

const PERMISSION_SECTIONS: readonly PermissionSection[] = [
  {
    id: 'testcases',
    labelKey: 'userInvite.berechtigungen.sections.testcases',
    icon: 'check_circle',
    items: [
      { key: 't_view', labelKey: 'userInvite.berechtigungen.perms.t_view', default: true },
      { key: 't_edit', labelKey: 'userInvite.berechtigungen.perms.t_edit', default: true },
      { key: 't_run', labelKey: 'userInvite.berechtigungen.perms.t_run', default: true },
      { key: 't_del', labelKey: 'userInvite.berechtigungen.perms.t_del', default: false },
    ],
  },
  {
    id: 'requirements',
    labelKey: 'userInvite.berechtigungen.sections.requirements',
    icon: 'edit',
    items: [
      { key: 'a_view', labelKey: 'userInvite.berechtigungen.perms.a_view', default: true },
      { key: 'a_edit', labelKey: 'userInvite.berechtigungen.perms.a_edit', default: true },
      { key: 'a_jira', labelKey: 'userInvite.berechtigungen.perms.a_jira', default: false },
    ],
  },
];

@Component({
  selector: 'app-user-invite-page',
  imports: [
    TranslatePipe,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    FormsModule,
    AvatarComponent,
    StatusPillComponent,
    RoleBadgeComponent,
    Modal,
    ModalEscapeGuardDirective,
  ],
  templateUrl: './user-invite-page.component.html',
  styleUrl: './user-invite-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserInvitePageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly tenantUserService = inject(TenantUserService);
  private readonly customerDetailService = inject(CustomerDetailService);
  private readonly accountService = inject(AccountService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly customer = signal<CustomerDTO | null>(null);
  readonly roles = signal<RoleDTO[]>([]);
  readonly invitableRoles = computed(() => this.roles().filter((r) => ASSIGNABLE_TENANT_ROLE_CODES.includes(r.code)));
  readonly loading = signal(true);
  readonly saving = signal(false);

  readonly activeTab = signal<InviteTab>('daten');

  readonly tabs: readonly { id: InviteTab; labelKey: string; icon: string }[] = [
    { id: 'daten', labelKey: 'userInvite.tabs.stammdaten', icon: 'group' },
    { id: 'rolle', labelKey: 'userInvite.tabs.berechtigungen', icon: 'shield' },
    { id: 'praeferenzen', labelKey: 'userInvite.tabs.praeferenzen', icon: 'settings' },
  ];

  readonly fieldLengths = { FIRST_LAST_NAME_MAX, EMAIL_MAX };

  readonly permissionSections = PERMISSION_SECTIONS;
  readonly languageOptions = LANGUAGE_OPTIONS;

  // Trigger des Zeitzonen-Autocompletes: erlaubt das Öffnen des Panels per
  // Dropdown-Pfeil (matSuffix), damit das Feld — wie im Design (SelectField) —
  // sichtbar als auswählbar erkennbar ist, obwohl es ein durchsuchbares Input ist.
  readonly timezoneTrigger = viewChild(MatAutocompleteTrigger);

  private readonly allTimezones = listTimezones();
  readonly timezoneSearch = signal('');
  readonly filteredTimezones = computed(() => {
    const term = this.timezoneSearch().trim();
    // Leerer Suchtext oder unverändertes (vorbelegtes) Label → volle Liste, damit
    // beim Öffnen alle Zonen sichtbar sind. Erst echtes Tippen filtert.
    const committedLabel = this.timezoneLabel(this.form.controls.timezone.value);
    if (!term || term === committedLabel) return this.allTimezones;
    const lower = term.toLowerCase();
    return this.allTimezones.filter((tz) => tz.toLowerCase().includes(lower));
  });
  // Die granularen Rechte sind — wie auf der Bearbeiten-Seite und im Design —
  // eine reine Frontend-Vorbelegung aus den Rollen-Defaults; sie werden (noch)
  // nicht ans Backend übertragen.
  readonly permissionState = signal<Record<string, boolean>>(this.initialPermissionState());

  readonly statusDisplay = 'userInvite.stammdaten.statusInvite';

  setTab(tab: InviteTab): void {
    this.activeTab.set(tab);
  }

  onTimezoneInput(term: string): void {
    this.timezoneSearch.set(term);
  }

  onTimezoneSelected(timezone: string): void {
    this.form.controls.timezone.setValue(timezone);
    // Das Anzeigefeld zeigt Zone + Offset; der Control-Wert bleibt die reine IANA-ID.
    this.timezoneSearch.set(this.timezoneLabel(timezone));
  }

  // Öffnet das Autocomplete-Panel über den Dropdown-Pfeil (matSuffix). Der Klick auf
  // den Pfeil fokussiert nicht das Input, daher öffnen wir das Panel explizit.
  openTimezonePanel(): void {
    this.timezoneTrigger()?.openPanel();
  }

  // Verwirft Freitext, der zu keiner gültigen Zeitzone gehört: das Feld zeigt wieder
  // den zuletzt committeten Wert des Controls (inkl. Offset-Label).
  onTimezoneBlur(): void {
    this.timezoneSearch.set(this.timezoneLabel(this.form.controls.timezone.value));
  }

  /** Aktueller UTC-Offset der Zone, z. B. "GMT+1" (leer, wenn nicht ermittelbar). */
  timezoneOffset(timezone: string): string {
    return timezoneOffset(timezone);
  }

  /** Text-Label fürs Suchfeld: „(GMT+1) Europe/Berlin" (Offset vorangestellt). */
  timezoneLabel(timezone: string): string {
    const offset = timezoneOffset(timezone);
    return offset ? `(${offset}) ${timezone}` : timezone;
  }

  private readonly customerId = signal('');
  private fromVerwaltung = false;

  // Vom Backend gemeldete Allow-List, sobald dessen Guard tatsächlich gegriffen hat. Sie
  // bleibt danach stehen und gewinnt: sie ist die Liste, gegen die maßgeblich geprüft wird —
  // die mit den Kundendaten geladene kann veraltet sein. Ohne das liefe der Nutzer in eine
  // Sackgasse, weil das Formular ihn gegen die veraltete Liste weiter durchwinkt.
  private readonly backendAllowedDomains = signal<string[] | null>(null);

  private readonly allowedDomains = computed(
    () => this.backendAllowedDomains() ?? this.customer()?.allowedDomains ?? [],
  );

  // Die Allow-List ist nur bindend, wenn der Kunde OTP zulässt (OTP oder BOTH): der
  // Accept-Weg einer Einladung läuft ausschließlich über OTP. Bei Entra-only greift ein
  // anderer Eignungsbegriff, die Liste bleibt dort ohne Wirkung. Hat das Backend die Regel
  // bereits angewandt, gilt sie unabhängig davon, was die geladenen Kundendaten sagen.
  private readonly allowListApplies = computed(() => {
    if (this.backendAllowedDomains() !== null) return true;
    const method = this.customer()?.authMethod;
    return method === 'OTP' || method === 'BOTH';
  });

  /** Erlaubte Domänen als Aufzählung für die Fehlermeldung am E-Mail-Feld. */
  readonly allowedDomainsLabel = computed(() => this.allowedDomains().join(', '));

  // Liest Signale, ist aber selbst nicht reaktiv — nach dem Laden der Kundendaten (und nach
  // einem 4xx des Backends) wird updateValueAndValidity() aufgerufen, damit ein bereits
  // getippter Wert gegen die dann gültige Liste nachgeprüft wird.
  private readonly allowedDomainValidator = (control: AbstractControl): ValidationErrors | null => {
    if (!this.allowListApplies()) return null;
    // Leeres oder unvollständiges Feld meldet bereits required/email — Fehler nicht stapeln.
    const value = String(control.value ?? '');
    if (emailDomain(value) === null) return null;
    return isDomainAllowed(value, this.allowedDomains()) ? null : { domainNotAllowed: true };
  };

  readonly form = this.fb.group({
    firstName: this.fb.control('', [Validators.required, Validators.maxLength(FIRST_LAST_NAME_MAX)]),
    lastName: this.fb.control('', [Validators.required, Validators.maxLength(FIRST_LAST_NAME_MAX)]),
    email: this.fb.control('', [
      Validators.required,
      Validators.email,
      Validators.maxLength(EMAIL_MAX),
      this.allowedDomainValidator,
    ]),
    roleId: this.fb.control('', [Validators.required]),
    // Sprache: fixer Default Deutsch (siehe DEFAULT_LANGUAGE). Zeitzone weiterhin
    // aus dem Browser vorbelegt (locale-abhängig sinnvoll). Explizite Nutzerwahl
    // überschreibt beides.
    language: this.fb.control(DEFAULT_LANGUAGE),
    timezone: this.fb.control(detectBrowserTimezone()),
  });

  // Die Rechte-Matrix zählt nicht: sie wird nicht gesendet, ginge also auch nach dem Einladen verloren.
  private readonly dirtyTracker = createFormDirtyTracker(this.form);
  readonly showDeactivateConfirm = signal(false);
  private readonly deactivateConfirm$ = new Subject<boolean>();

  private readonly formValue = toSignal(this.form.valueChanges, { initialValue: this.form.getRawValue() });
  private readonly formStatus = toSignal(this.form.statusChanges, { initialValue: this.form.status });

  readonly previewName = computed(() => {
    const { firstName, lastName } = this.formValue();
    return `${firstName ?? ''} ${lastName ?? ''}`.trim();
  });

  readonly previewEmail = computed(() => this.formValue().email ?? '');

  readonly selectedRoleCode = computed(() => {
    const roleId = this.formValue().roleId;
    return this.roles().find((r) => r.id === roleId)?.code ?? '';
  });

  readonly selectedRoleLabel = computed(() => {
    const roleId = this.formValue().roleId;
    return this.roles().find((r) => r.id === roleId)?.displayName ?? '—';
  });

  readonly canSubmit = computed(() => this.formStatus() === 'VALID' && !this.saving());

  constructor() {
    warnBeforeUnload(() => this.hasUnsavedChanges());
  }

  ngOnInit(): void {
    const routeCustomerId = this.route.snapshot.paramMap.get('customerId');
    this.fromVerwaltung = routeCustomerId === null;

    // Suchfeld zeigt initial die vorbelegte (Browser-)Zeitzone samt Offset.
    this.timezoneSearch.set(this.timezoneLabel(this.form.controls.timezone.value));

    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.cdr.markForCheck());
    this.form.controls.email.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.clearServerSideEmailErrors());

    // Aus der Verwaltung lädt ein Mandanten-Admin in den eigenen Kunden ein. Die Kundendaten
    // gibt es nur für Platform Admins; ohne sie prüft allein das Backend die Domäne (422).
    const customerId$ =
      routeCustomerId !== null
        ? of(routeCustomerId)
        : this.accountService.getProfile().pipe(
            map((profile) => profile.customerId),
            catchError(() => of('')),
          );

    customerId$
      .pipe(
        switchMap((customerId) => {
          this.customerId.set(customerId);
          if (!customerId) return of({ customer: null, roles: [] });
          return forkJoin({
            customer: this.fromVerwaltung
              ? of(null)
              : this.customerDetailService.getCustomer(customerId).pipe(catchError(() => of(null))),
            roles: this.tenantUserService.getRoles(customerId).pipe(catchError(() => of([]))),
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ customer, roles }) => {
        if (customer) {
          this.customer.set(customer);
          // Die Allow-List steht erst jetzt fest — eine bereits eingegebene Adresse nachprüfen.
          this.form.controls.email.updateValueAndValidity({ emitEvent: false });
        }
        this.roles.set(roles);
        this.loading.set(false);
        this.cdr.markForCheck();
      });
  }

  onSubmit(): void {
    if (this.form.invalid || this.saving()) return;
    const customerId = this.customerId();
    if (!customerId) return;

    this.saving.set(true);
    const request: InviteUserRequest = this.form.getRawValue();

    this.tenantUserService
      .invite(customerId, request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (invitation) => {
          this.saving.set(false);
          if (invitation.mailDelivered === false) {
            this.snackBar.open(
              this.translate.instant('userInvite.mailNotDelivered'),
              undefined,
              MAIL_NOT_DELIVERED_SNACKBAR,
            );
          } else {
            this.snackBar.open(this.translate.instant('userInvite.success'), undefined, {
              panelClass: 'snackbar-success',
            });
          }
          this.dirtyTracker.reset();
          this.navigateBack();
        },
        error: (err: HttpErrorResponse) => {
          this.saving.set(false);
          if (err.status === 409) {
            this.markEmailError('duplicate');
            return;
          }
          // Das Backend ist die maßgebliche Absicherung: lehnt es die Domäne ab, zeigt das
          // Formular dieselbe Inline-Meldung — mit der Liste, gegen die dort geprüft wurde.
          const body = err.error as { code?: string; allowedDomains?: string[] } | null;
          if (err.status === 422 && body?.code === 'INVITATION_DOMAIN_NOT_ALLOWED') {
            this.backendAllowedDomains.set(body.allowedDomains ?? []);
            this.markEmailError('domainNotAllowed');
            return;
          }
          this.snackBar.open(this.translate.instant('userInvite.error'), undefined, {
            panelClass: 'snackbar-error',
          });
        },
      });
  }

  private markEmailError(error: 'duplicate' | 'domainNotAllowed'): void {
    this.form.controls.email.setErrors({ [error]: true });
    this.form.controls.email.markAsTouched();
    this.cdr.markForCheck();
  }

  // Serverseitig gesetzte Fehler gelten nur für den abgeschickten Wert. Sobald der Nutzer
  // die Adresse ändert, laufen wieder die Validatoren — die gemeldete Allow-List bleibt dabei
  // erhalten, damit die Prüfung weiter gegen die maßgebliche Liste läuft.
  private clearServerSideEmailErrors(): void {
    const email = this.form.controls.email;
    if (email.hasError('duplicate') || email.hasError('domainNotAllowed')) {
      email.updateValueAndValidity({ emitEvent: false });
    }
  }

  isPermissionChecked(key: string): boolean {
    return this.permissionState()[key] ?? false;
  }

  togglePermission(key: string, checked: boolean): void {
    this.permissionState.update((state) => ({ ...state, [key]: checked }));
  }

  navigateBack(): void {
    if (this.fromVerwaltung) {
      void this.router.navigate(['/settings'], { queryParams: { bereich: 'benutzer' } });
      return;
    }
    void this.router.navigate(['/client-management', this.customerId()]);
  }

  hasUnsavedChanges(): boolean {
    return this.dirtyTracker.isDirty() && !this.saving();
  }

  confirmDeactivate(): Observable<boolean> {
    this.showDeactivateConfirm.set(true);
    return this.deactivateConfirm$.pipe(take(1));
  }

  onDeactivateConfirmed(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(true);
  }

  onDeactivateCancelled(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(false);
  }

  private initialPermissionState(): Record<string, boolean> {
    const state: Record<string, boolean> = {};
    for (const section of PERMISSION_SECTIONS) {
      for (const item of section.items) {
        state[item.key] = item.default;
      }
    }
    return state;
  }
}
